import { alpha, Chip, styled } from '@mui/material';

import { isDarkMode } from '@localstack/services';

/**
 * The product's "NEW" marker. Extracted from the Getting Started page so every surface
 * that announces a new feature uses the same badge rather than restyling a Chip locally.
 *
 * Amber rather than primary on purpose: primary is the app's action colour, and a badge
 * is not actionable. Keeping "new" on its own hue means it reads as an annotation
 * wherever it lands.
 */
export const NewBadge = styled(Chip)(({ theme }) => ({
  height: 22,
  // 4px, not the Chip default pill: at a 10px uppercase label a fully round end reads as
  // a marketing sticker. This keeps it on the same corner language as the surfaces it
  // sits on.
  borderRadius: theme.spacing(0.5),
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '0.08em',
  border: `1px solid ${
    isDarkMode(theme) ? alpha(theme.palette.warning.main, 0.55) : alpha(theme.palette.warning.dark, 0.22)
  }`,
  backgroundColor: alpha(theme.palette.warning.main, isDarkMode(theme) ? 0.18 : 0.14),
  // The lighter amber is deliberate in dark mode to preserve readable contrast.
  color: isDarkMode(theme) ? '#FCD34D' : theme.palette.warning.dark,
  boxShadow: isDarkMode(theme)
    ? `0 0 0 1px ${alpha(theme.palette.warning.main, 0.08)}, 0 6px 14px ${alpha(theme.palette.common.black, 0.2)}`
    : `0 4px 10px ${alpha(theme.palette.warning.dark, 0.08)}`,
  textDecoration: 'none',
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
  },
  '&.MuiChip-clickable': {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: alpha(theme.palette.warning.main, isDarkMode(theme) ? 0.24 : 0.2),
      borderColor: isDarkMode(theme)
        ? alpha(theme.palette.warning.main, 0.75)
        : alpha(theme.palette.warning.dark, 0.32),
    },
  },
})) as typeof Chip;
